import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  APP_TYPE,
  clearSelectedItemsMap,
  CreatePaymentTransactionRequest,
  dispatch,
  ErrorVariants,
  getAppType,
  getCurrentCustomer,
  getPaymentCustomerInfo,
  getSelectedItems,
  getUserData,
  getWorkspaceId,
  OfflineTaskType,
  PaymentDetailsMap,
  showToast,
  updateCollectPaymentSuccess,
  updateSelectedItemIds,
  useAppSelector,
  useCreatePaymentTransactionMutation,
  useUploadWorkspaceDocumentsMutation,
} from '@zonofi/zono-money-store';
import { FIELD_OPS_ROUTES, PaymentModeType } from '../../../constants';
import { convertToISTDateTime } from '../../helper';
import { sanitizeValue, useCustomNavigate } from '../../../helper';
import { isEmpty } from 'lodash';
import { UploadToS3 } from '@zonofi/common/functions/s3-upload';
import { FileUploadOutput } from '@zonofi/common/types/interface';
import { addTaskToQueue } from '../../../queue/offline';
import { v4 as uuidv4 } from 'uuid';

const getPaymentModeKey = (value: string) => {
  const entry = Object.entries(PaymentModeType).find(
    ([key, val]) => val === value
  );
  return entry ? entry[0] : value;
};

interface UseSavePaymentProps {
  paymentDetails: PaymentDetailsMap;
  paymentId?: string | undefined;
  isOfflineUpdate?: boolean;
  paymentType: string;
  uploadedMedia: FileUploadOutput[];
  setSaveLoader: Dispatch<SetStateAction<boolean>>;
}

export const useSavePayment = ({
  paymentDetails,
  paymentId = '',
  isOfflineUpdate,
  paymentType,
  uploadedMedia,
  setSaveLoader,
}: UseSavePaymentProps) => {
  const { navigate, onBack } = useCustomNavigate();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const paymentCustomerInfo = useAppSelector(getPaymentCustomerInfo);
  const selectedInvoiceItems = useAppSelector(getSelectedItems);
  const apptype = useAppSelector(getAppType);
  const currentCustomer = useAppSelector(getCurrentCustomer);
  const userData = useAppSelector(getUserData);

  const [createPaymentTransaction, createPaymentTransactionResponse] =
    useCreatePaymentTransactionMutation();
  const [uploadDocument] = useUploadWorkspaceDocumentsMutation();

  const createPaymentPayload = () => {
    const paymentDate = convertToISTDateTime(
      String(paymentDetails.paymentDate ?? ''),
      String(paymentDetails.paymentTime ?? '')
    );

    const paymentMode = getPaymentModeKey(String(paymentDetails?.paymentMode));

    const payload: CreatePaymentTransactionRequest = {
      sellerWorkspaceId: sellerWorkspaceId,
      inviteId: sanitizeValue(paymentCustomerInfo?.inviteId),
      createdBy: paymentId
        ? sanitizeValue(paymentCustomerInfo?.createdBy)
        : userData.id,
      paymentDate: paymentDate,
      paymentMode: paymentMode,
      amountPaid: Number(String(paymentDetails?.amountPaid)?.replace(/,/g, '')),
      doctype: 'PYMNT',
      currencyCode: 'INR',
      bankReference: sanitizeValue(paymentDetails?.bankReference),
      bankName: sanitizeValue(paymentDetails?.bankName),
      bankAccountNumber: sanitizeValue(paymentDetails?.bankAccountNumber),
      upiId: sanitizeValue(paymentDetails?.upiId),
      paymentType: paymentType === 'ONLINE' ? 'ONLN' : 'OFLN',
      paymentRefCode: sanitizeValue(paymentDetails?.paymentRefCode),
      instrumentNumber: sanitizeValue(paymentDetails?.instrumentNumber),
      cardNumber: sanitizeValue(paymentDetails?.cardNumber),
      cardType: sanitizeValue(paymentDetails?.cardType),
      paymentProvider: sanitizeValue(paymentDetails?.paymentProvider),
      narration: sanitizeValue(paymentDetails?.narration),
      ...(paymentId ? { paymentTransactionId: paymentId } : {}),
    };

    if (selectedInvoiceItems && selectedInvoiceItems.length > 0) {
      payload.invoices = selectedInvoiceItems.map((invoice) => ({
        docNumber: invoice?.docNumber,
      }));
    }

    return payload;
  };

  const onSavePayment = async () => {
    const paymentPayload = createPaymentPayload();
    if (paymentId) {
      setSaveLoader(true);
      await createPaymentTransaction(paymentPayload);
      return;
    }
    if (apptype === APP_TYPE.SELLER_HUB) {
      setSaveLoader(true);
      await createPaymentTransaction(paymentPayload);
      return;
    }
    if (apptype === APP_TYPE.FIELDS_OPS) {
      const paymentTempId = paymentDetails.tempId?.toString() ?? uuidv4();
      await addTaskToQueue({
        type: OfflineTaskType.COLLECT_PAYMENT,
        customer: currentCustomer,
        entityKey: `${OfflineTaskType.COLLECT_PAYMENT.toLowerCase()}:${paymentCustomerInfo?.inviteId}:${paymentTempId}`,
        payload: {
          paymentTempId,
          data: paymentPayload,
          media: uploadedMedia,
          selectedInvoices: selectedInvoiceItems,
          customerInfo: {
            customerName: sanitizeValue(paymentCustomerInfo?.customerName),
            customerId: sanitizeValue(paymentCustomerInfo?.inviteId),
          },
        },
      });
      dispatch(clearSelectedItemsMap());
      dispatch(updateSelectedItemIds([]));
      if (isOfflineUpdate) {
        navigate(FIELD_OPS_ROUTES.SYNC);
        dispatch(
          showToast({
            message: 'Payment has been updated successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
      } else {
        onBack();
        dispatch(
          showToast({
            message: 'Payment has been recorded successfully',
            type: ErrorVariants.SUCCESS,
            canDisplay: true,
            responseStatus: 'Success',
          })
        );
      }
    }
  };

  const naviagteSuccess = () => {
    if (!paymentId) {
      dispatch(
        updateCollectPaymentSuccess({
          showSuccess: true,
          amount: Number(paymentDetails.amountPaid),
          customerName: String(paymentCustomerInfo?.customerName),
          mode: String(paymentDetails?.paymentMode),
        })
      );
    }
    dispatch(clearSelectedItemsMap());
    setSaveLoader(false);
    dispatch(
      showToast({
        canDisplay: true,
        message: paymentId
          ? 'Changes saved Successfully'
          : 'Payment created Successfully',
        responseStatus: 'Success',
        type: ErrorVariants.SUCCESS,
      })
    );
    onBack();
  };

  useEffect(() => {
    if (
      createPaymentTransactionResponse.isSuccess &&
      createPaymentTransactionResponse.data
    ) {
      if (uploadedMedia.length > 0) {
        uploadPaymentDocuments(createPaymentTransactionResponse.data?.id);
      } else {
        naviagteSuccess();
      }
    } else if (createPaymentTransactionResponse.isError) {
      setSaveLoader(false);
      dispatch(
        showToast({
          canDisplay: true,
          message: paymentId
            ? 'Failed to update payment'
            : 'Failed to create payment',
          responseStatus: 'Error',
          type: ErrorVariants.DANGER,
        })
      );
    }
  }, [createPaymentTransactionResponse]);

  const uploadPaymentDocuments = async (id: string) => {
    try {
      const filesToUpload = uploadedMedia.filter(
        (file) => !(file?.uri as string)?.startsWith('https://')
      );
      if (filesToUpload.length === 0) return;

      const maxSequenceNo = uploadedMedia
        .filter((file) => (file?.uri as string)?.startsWith('https://'))
        .reduce((max, file) => {
          const sequenceMatch = file.name.match(/^(\d+)\//);
          if (sequenceMatch) {
            const currentSeq = parseInt(sequenceMatch[1], 10);
            return Math.max(max, isNaN(currentSeq) ? 0 : currentSeq);
          }
          return max;
        }, 0);

      const uploadPromises = filesToUpload.map(async (item, index) => {
        const sequenceNo = (maxSequenceNo + index + 1).toString();
        const response = await uploadDocument({
          workspaceId: sellerWorkspaceId,
          documentType: 'payment',
          entityId: id,
          mimeType: item?.type || 'image/jpeg',
          subType: 'transactions',
          sequenceNo: sequenceNo,
        }).unwrap();

        if (response?.presignedUrl && item.uri) {
          await UploadToS3(response.presignedUrl, {
            uri: item?.uri as string,
            type: item?.type || 'image/jpeg',
          });
        }
      });

      await Promise.all(uploadPromises);
    } catch (error) {
      if (!isEmpty(error)) {
        setTimeout(() => {
          dispatch(
            showToast({
              canDisplay: true,
              message: 'Failed to upload file(s)',
              responseStatus: 'Error',
              type: ErrorVariants.DANGER,
            })
          );
        }, 100);
      }
    } finally {
      naviagteSuccess();
    }
  };

  return { onSavePayment };
};
